import React, { useEffect, useState } from "react";
import "./FabricForm.css";
import { backend_url } from "../../App";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TEXT_FIELDS = [
  { key: "fabricType", label: "Fabric Type" },
  { key: "colour", label: "Colour" },
  { key: "printCategory", label: "Print Category" },
  { key: "gsm", label: "GSM", type: "number" },
  { key: "width", label: "Width" },
  { key: "unit", label: "Stock Unit (e.g. meters)" },
  { key: "stockQuantity", label: "Stock Quantity", type: "number" },
  { key: "costPerUnit", label: "Cost per Unit (₹)", type: "number" },
  { key: "supplierName", label: "Supplier Name" },
  { key: "supplierContact", label: "Supplier Contact" },
];

const emptyForm = () => TEXT_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), { unit: "meters" });

const FabricForm = ({ fabricId, onClose, onSaved }) => {
  const isNew = !fabricId;
  const [form, setForm] = useState(emptyForm());
  const [images, setImages] = useState([]); // existing image URLs (edit mode)
  const [newFiles, setNewFiles] = useState([]); // File objects to upload
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [record, setRecord] = useState(null);

  useEffect(() => {
    if (!isNew) fetchFabric();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fabricId]);

  const fetchFabric = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backend_url}/api/fabrics/${fabricId}`);
      const data = await res.json();
      if (!data.success) throw new Error();
      setRecord(data.fabric);
      const f = emptyForm();
      TEXT_FIELDS.forEach((field) => { f[field.key] = data.fabric[field.key] ?? ""; });
      setForm(f);
      setImages(data.fabric.images || []);
    } catch {
      toast.error("Failed to load fabric");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (images.length + newFiles.length + files.length > 6) {
      toast.error("Max 6 images allowed");
      return;
    }
    setNewFiles((p) => [...p, ...files]);
  };

  const removeNewFile = (i) => setNewFiles((p) => p.filter((_, idx) => idx !== i));

  const removeExistingImage = async (url) => {
    if (isNew) return;
    try {
      const res = await fetch(`${backend_url}/api/fabrics/${fabricId}/images`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageUrl: url }),
      });
      const data = await res.json();
      if (!data.success) throw new Error();
      setImages((p) => p.filter((img) => img !== url));
    } catch {
      toast.error("Failed to remove image");
    }
  };

  const handleSave = async () => {
    if (!form.fabricType?.trim()) {
      toast.error("Fabric type is required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const fd = new FormData();
        TEXT_FIELDS.forEach((f) => fd.append(f.key, form[f.key] ?? ""));
        newFiles.forEach((file) => fd.append("images", file));

        const res = await fetch(`${backend_url}/api/fabrics`, { method: "POST", body: fd });
        const data = await res.json();
        if (!data.success) throw new Error(data.message);
        toast.success("Fabric added");
        onSaved && onSaved(data.fabric);
        onClose();
      } else {
        const res = await fetch(`${backend_url}/api/fabrics/${fabricId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.message);

        if (newFiles.length > 0) {
          const fd = new FormData();
          newFiles.forEach((file) => fd.append("images", file));
          const imgRes = await fetch(`${backend_url}/api/fabrics/${fabricId}/images`, { method: "POST", body: fd });
          const imgData = await imgRes.json();
          if (imgData.success) setImages(imgData.fabric.images);
          setNewFiles([]);
        }

        toast.success("Fabric updated");
        setRecord(data.fabric);
        onSaved && onSaved(data.fabric);
      }
    } catch {
      toast.error("Failed to save fabric");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fform-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="fform-modal">
          <button className="fform-close" onClick={onClose}>✕</button>

          <div className="fform-header">
            <h2>{isNew ? "Add New Fabric" : form.fabricType || "Fabric"}</h2>
            {record?.fabricId && <span className="fform-id-chip">{record.fabricId}</span>}
          </div>

          <div className="fform-body">
            {loading ? (
              <p className="fform-loading">Loading…</p>
            ) : (
              <>
                <div className="fform-images-section">
                  <span className="fform-images-label">Fabric Images</span>
                  <div className="fform-images-row">
                    {images.map((url) => (
                      <div key={url} className="fform-img-thumb">
                        <img src={url} alt="fabric" />
                        <button onClick={() => removeExistingImage(url)}>✕</button>
                      </div>
                    ))}
                    {newFiles.map((file, i) => (
                      <div key={i} className="fform-img-thumb fform-img-pending">
                        <img src={URL.createObjectURL(file)} alt="pending" />
                        <button onClick={() => removeNewFile(i)}>✕</button>
                      </div>
                    ))}
                    {images.length + newFiles.length < 6 && (
                      <label className="fform-img-upload-zone">
                        + Add
                        <input type="file" accept="image/*" multiple hidden onChange={handleFileSelect} />
                      </label>
                    )}
                  </div>
                </div>

                <div className="fform-grid">
                  {TEXT_FIELDS.map((f) => (
                    <label key={f.key} className="fform-field">
                      <span>{f.label}</span>
                      <input
                        type={f.type || "text"}
                        value={form[f.key]}
                        onChange={(e) => handleChange(f.key, e.target.value)}
                      />
                    </label>
                  ))}
                </div>

                <div className="fform-actions">
                  <button className="fform-save-btn" disabled={saving} onClick={handleSave}>
                    {saving ? "Saving…" : isNew ? "Add Fabric" : "Save Changes"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={2500} />
    </>
  );
};

export default FabricForm;
